import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Table,
  Input,
  Breadcrumb,
  Tag,
  Modal,
  Select,
  message,
  Button,
  DatePicker,
  Space,
  Grid,
  Row,
  Col,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  RightOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { io } from 'socket.io-client';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// import { LoadableContent } from '../../components';
import { AuthContext } from '../../common/constants';
import { demandeAssistancesService } from '../../common/services';

const { RangePicker } = DatePicker;
// const socket = io('http://localhost:8081');
const socket = io('https://api.tigoon-connect.com/api/v1');

const STATUS_META = {
  en_attente:  { label: 'En attente',  color: 'warning',    icon: <ClockCircleOutlined /> },
  en_cours:    { label: 'En cours',     color: 'processing', icon: <SyncOutlined spin /> },
  resolue:     { label: 'Résolue',      color: 'success',    icon: <CheckCircleOutlined /> },
  non_resolue: { label: 'Non résolue',  color: 'error',      icon: <CloseCircleOutlined /> },
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let local = phone.replace('+225', '').trim();
  return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const DemandesAssistances = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // filtres / recherche
  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs]
  const [statutFilter, setStatutFilter] = useState(undefined); // optionnel

  // pagination / tri
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-createdAt');

  // modal traitement
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedStatut, setSelectedStatut] = useState('');
  const [commentaire, setCommentaire] = useState('');

  const { currentUser } = useContext(AuthContext);

  // 🔹 Responsive helpers
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;                      // < 576px
  const controlSize = isMobile ? 'small' : 'middle';
  const fullW = { width: isMobile ? '100%' : 'auto' };

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page, limit, sort,
        q: q || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end:   dateRange?.[1]?.format('YYYY-MM-DD'),
        statut: statutFilter || undefined,
      };
      const data = await demandeAssistancesService.getList(params);
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error(e);
      message.error("Erreur lors du chargement des demandes");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, q, dateRange, statutFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // WebSocket -> rechargement live
  useEffect(() => {
    const onNew = () => fetchList();
    const onUpdated = () => fetchList();

    socket.on('nouvelle-demande', onNew);
    socket.on('demande_assistance_updated', onUpdated);

    return () => {
      socket.off('nouvelle-demande', onNew);
      socket.off('demande_assistance_updated', onUpdated);
    };
  }, [fetchList]);

  // EXPORTS — refetch tout l’ensemble filtré (pas juste la page courante)
  const fetchAllForExport = async () => {
    const params = {
      page: 1, limit: 5000, sort,
      q: q || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end:   dateRange?.[1]?.format('YYYY-MM-DD'),
      statut: statutFilter || undefined,
    };
    const data = await demandeAssistancesService.getList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    try {
      const all = await fetchAllForExport();
      const rows = all.map(d => ({
        Nom: d.utilisateur?.prenom || '',
        Prénom: d.utilisateur?.nom || '',
        Téléphone: formatPhoneNumber(d.utilisateur?.telephone || ''),
        Motif: d.motif || '',
        Message: d.message || '',
        Commentaire: d.commentaire || '',
        Statut: STATUS_META[d.statut]?.label || d.statut || '',
        'Traité par': d.adminTraiteur ? `${d.adminTraiteur.prenom} ${d.adminTraiteur.nom}` : '—',
        'Créée le': d.createdAt || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Demandes_assistance');
      XLSX.writeFile(wb, 'demandes_assistance.xlsx');
    } catch {
      message.error('Export Excel: une erreur est survenue.');
    }
  };

  const exportCSV = async () => {
    try {
      const all = await fetchAllForExport();
      const rows = all.map(d => ({
        Nom: d.utilisateur?.prenom || '',
        Prenom: d.utilisateur?.nom || '',
        Telephone: formatPhoneNumber(d.utilisateur?.telephone || ''),
        Motif: d.motif || '',
        Message: d.message || '',
        Commentaire: d.commentaire || '',
        Statut: STATUS_META[d.statut]?.label || d.statut || '',
        TraitePar: d.adminTraiteur ? `${d.adminTraiteur.prenom} ${d.adminTraiteur.nom}` : '—',
        CreeLe: d.createdAt || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'demandes_assistance.csv');
    } catch {
      message.error('Export CSV: une erreur est survenue.');
    }
  };

  const exportPDF = async () => {
    try {
      const all = await fetchAllForExport();
      const doc = new jsPDF();
      doc.text("Demandes d’assistance", 14, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Nom', 'Prénom', 'Téléphone', 'Motif', 'Statut', 'Traité par', 'Créée le']],
        body: all.map(d => [
          d.utilisateur?.prenom || '',
          d.utilisateur?.nom || '',
          formatPhoneNumber(d.utilisateur?.telephone || ''),
          d.motif || '',
          STATUS_META[d.statut]?.label || d.statut || '',
          d.adminTraiteur ? `${d.adminTraiteur.prenom} ${d.adminTraiteur.nom}` : '—',
          d.createdAt || '',
        ]),
      });
      doc.save('demandes_assistance.pdf');
    } catch {
      message.error('Export PDF: une erreur est survenue.');
    }
  };

  const openModal = (demande) => {
    setSelectedDemande(demande);
    setSelectedStatut(demande.statut || 'en_attente');
    setCommentaire(demande.commentaire || '');
    setModalVisible(true);
  };

  const handleTraitement = async () => {
    if (!selectedStatut) return message.warning("Veuillez sélectionner un statut");
    if (selectedStatut === 'non_resolue' && !commentaire.trim()) {
      return message.warning("Veuillez fournir un commentaire pour les demandes non résolues");
    }
    try {
      await demandeAssistancesService.traiterDemandeAssistance(selectedDemande.id, {
        statut: selectedStatut,
        adminId: currentUser?._id,
        commentaireResolution: commentaire,
      });
      message.success("Demande mise à jour avec succès");
      setModalVisible(false);
      fetchList();
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la mise à jour");
    }
  };

  const columns = [
    { title: 'Nom', dataIndex: ['utilisateur', 'prenom'], key: 'utilisateur.prenom', sorter: true },
    { title: 'Prénom', dataIndex: ['utilisateur', 'nom'], key: 'utilisateur.nom', sorter: true },
    {
      title: 'Téléphone',
      dataIndex: ['utilisateur', 'telephone'],
      key: 'utilisateur.telephone',
      sorter: true,
      render: (v) => formatPhoneNumber(v),
    },
    { title: 'Motif', dataIndex: 'motif', key: 'motif', sorter: true },
    { title: 'Message', dataIndex: 'message', key: 'message' },
    { title: 'Commentaire', dataIndex: 'commentaire', key: 'commentaire' },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (d) => d || '',
    },
    {
      title: 'Statut',
      dataIndex: 'statut',
      key: 'statut',
      sorter: true,
      render: (statut) => {
        const meta = STATUS_META[statut] || { label: '—', color: 'default' };
        return <Tag color={meta.color} icon={meta.icon} style={{ fontWeight: 600 }}>{meta.label}</Tag>;
      },
      filters: [
        { text: 'En attente', value: 'en_attente' },
        { text: 'En cours', value: 'en_cours' },
        { text: 'Résolue', value: 'resolue' },
        { text: 'Non résolue', value: 'non_resolue' },
      ],
    },
    {
      title: 'Traité par',
      key: 'adminTraiteur',
      sorter: true,
      dataIndex: 'adminTraiteur',
      render: (adm) => (adm ? `${adm.prenom} ${adm.nom}` : '—'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const statut = record?.statut;
        const adminEnCoursId =
          record?.adminTraiteur?.id ?? record?.adminTraiteur?._id ?? null;
        const currentId = currentUser?._id ?? currentUser?.id ?? null;
        const sameAdmin = adminEnCoursId && currentId && String(adminEnCoursId) === String(currentId);
        const canSeeTraiter =
          statut === 'en_attente' || statut === 'non_resolue' || (statut === 'en_cours' && sameAdmin);
        if (statut === 'resolue') return null;
        return canSeeTraiter ? (
          <Button type="primary" size={controlSize} onClick={() => openModal(record)} style={fullW}>
            Traiter
          </Button>
        ) : null;
      },
    },
  ];

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    // statut via filtre UI -> envoyons-le au serveur
    if (filters?.statut && filters.statut.length) {
      setStatutFilter(filters.statut[0]);
      setPage(1);
    } else if (statutFilter) {
      setStatutFilter(undefined);
      setPage(1);
    }

    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      const allowed = [
        'createdAt','statut','motif',
        'utilisateur.nom','utilisateur.prenom','utilisateur.telephone',
        'adminTraiteur.nom','adminTraiteur.prenom'
      ];
      if (allowed.includes(key)) setSort(`${dir}${key}`);
    }
  };

  return (
    // <LoadableContent isLoading={isLoading}>
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="row">
          <div className="col-md-12">
            <Breadcrumb
              separator={<RightOutlined style={{ fontSize: '16px', color: '#999', fontWeight: 'bolder' }} />}
              style={{ marginBottom: 16 }}
            >
              <Breadcrumb.Item>
                <Link to="/dashboard" style={{ fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <span style={{ fontWeight: 'bolder' }}>📨 DEMANDES D’ASSISTANCE</span>
              </Breadcrumb.Item>
            </Breadcrumb>

            <div className="card mb-4">
              <div className="card-body">
                {/* 🔎 Filtres en grille (responsive) */}
                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                  <Col xs={24} md={10}>
                    <Input
                      prefix={<SearchOutlined />}
                      placeholder="Rechercher (tous champs)"
                      value={q}
                      onChange={(e) => { setQ(e.target.value); setPage(1); }}
                      allowClear
                      size={controlSize}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <RangePicker
                      onChange={(r) => { setDateRange(r); setPage(1); }}
                      placeholder={['Date début', 'Date fin']}
                      format="DD/MM/YYYY"
                      allowClear
                      size={controlSize}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Select
                      allowClear
                      placeholder="Filtrer par statut"
                      value={statutFilter}
                      onChange={(v) => { setStatutFilter(v); setPage(1); }}
                      size={controlSize}
                      style={{ width: '100%' }}
                      options={[
                        { value: 'en_attente', label: 'En attente' },
                        { value: 'en_cours', label: 'En cours' },
                        { value: 'resolue', label: 'Résolue' },
                        { value: 'non_resolue', label: 'Non résolue' },
                      ]}
                    />
                  </Col>
                </Row>

                {/* ⬇️ Boutons d'export responsives */}
                <div style={{ marginBottom: 16 }}>
                  <Space wrap size={[8, 8]} style={{ width: '100%' }}>
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={exportExcel}
                      type="primary"
                      size={controlSize}
                      style={fullW}
                    >
                      Exporter Excel (.xlsx)
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={exportCSV}
                      size={controlSize}
                      style={fullW}
                    >
                      Exporter CSV
                    </Button>
                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={exportPDF}
                      danger
                      size={controlSize}
                      style={fullW}
                    >
                      Exporter PDF
                    </Button>
                  </Space>
                </div>

                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={items}
                  loading={isLoading}
                  onChange={onTableChange}
                  size={controlSize}
                  scroll={{ x: true }}
                  pagination={{
                    current: page,
                    pageSize: limit,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [5, 10, 20, 50, 100],
                    showLessItems: isMobile,
                  }}
                />
              </div>
            </div>

            {/* 🔧 Modal traitement responsive */}
            <Modal
              title="Traiter la demande d'assistance"
              open={isModalVisible}
              onOk={handleTraitement}
              onCancel={() => setModalVisible(false)}
              okText="Mettre à jour"
              cancelText="Annuler"
              centered
              width={isMobile ? 380 : 520}
              bodyStyle={{ padding: isMobile ? 12 : 24 }}
              okButtonProps={{ size: controlSize, style: fullW }}
              cancelButtonProps={{ size: controlSize, style: fullW }}
              destroyOnClose
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Statut :</div>
                  <Select
                    value={selectedStatut}
                    onChange={setSelectedStatut}
                    style={{ width: '100%' }}
                    size={controlSize}
                    options={[
                      { value: 'en_cours', label: 'En cours' },
                      { value: 'resolue', label: 'Résolue' },
                      { value: 'non_resolue', label: 'Non résolue' },
                    ]}
                  />
                </div>

                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Commentaire :</div>
                  <Input.TextArea
                    value={commentaire}
                    onChange={(e) => setCommentaire(e.target.value)}
                    rows={isMobile ? 3 : 4}
                    placeholder="Entrez un commentaire (obligatoire si non résolue)"
                  />
                </div>
              </Space>
            </Modal>

          </div>
        </div>
      </div>
    </div>
    // </LoadableContent>
  );
};

export default DemandesAssistances;
